<template>
    <div>
        <span v-if="$scopedSlots['input']">
        <slot
                name="input"
                v-bind:attrs="$attrs"
                v-bind:listeners="$listeners"
                :ref="input"
        ></slot>
    </span>
        <template v-else-if="!$scopedSlots['input']">
            <input  ref="input"
                    v-bind="$attrs"
                    v-on="$listeners"
                    @input="onChangeInput"
                    class="inputAutocomplete"
                    :value="address"
                    @change="changeValueAddress"
            />
        </template>
    </div>
</template>

<script>
    import {gmapApi} from 'gmap-vue';

    const mappedProps = {
        bounds: {
            type: Object,
        },
        componentRestrictions: {
            type: Object,
            // Do not bind -- must check for undefined
            // in the property
            noBind: true,
        },
        types: {
            type: Array,
            default() {
                return [];
            },
        },
    };

    const props = {
        selectFirstOnEnter: {
            required: false,
            type: Boolean,
            default: false,
        },
        // the name of the ref to obtain the input (if its a child  of component in the slot)
        childRefName: {
            required: false,
            type: String,
            default: 'input',
        },
        options: {
            type: Object,
        },
        fields: {
            required: false,
            type: Array,
            default: null,
        },
    };

    function mappedPropsToVueProps(mappedProps) {
        return Object.entries(mappedProps)
            .map(([key, prop]) => {
                const value = {};

                if ('type' in prop) value.type = prop.type;
                if ('default' in prop) value.default = prop.default;
                if ('required' in prop) value.required = prop.required;

                return [key, value];
            })
            .reduce((acc, [key, val]) => {
                acc[key] = val;
                return acc;
            }, {});
    }

    export default {
        name: "MapAutocomplete",
        model: {
            prop: 'address',
            event: 'changeAddress'
        },
        props: {
            ...mappedPropsToVueProps(mappedProps),
            ...props,
            address: String
        },
        mounted() {
            this.$gmapApiPromiseLazy().then(() => {
                let scopedInput = null;
                if (this.$scopedSlots.input) {
                    scopedInput = this.$scopedSlots.input()[0].context.$refs.input;
                    if (scopedInput && scopedInput.$refs) {
                        scopedInput = scopedInput.$refs[this.childRefName || 'input'];
                    }
                    if (scopedInput) {
                        this.$refs.input = scopedInput;
                    }
                }
                if (this.selectFirstOnEnter) {
                    this.simulateArrowDown(this.$refs.input);
                }

                if (typeof this.google.maps.places.Autocomplete !== 'function') {
                    throw new Error(
                        "google.maps.places.Autocomplete is undefined. Did you add 'places' to libraries when loading Google Maps?"
                    );
                }

                const finalOptions = {
                    ...this.getPropsValues(this, mappedProps),
                    ...this.options,
                };

                console.log(this.$refs.input);
                this.$autocomplete = new this.google.maps.places.Autocomplete(
                    this.$refs.input,
                    finalOptions
                );
                this.bindProps(this, this.$autocomplete, mappedProps);

                this.$watch('componentRestrictions', (v) => {
                    if (v !== undefined) {
                        this.$autocomplete.setComponentRestrictions(v);
                    }
                });

                // IMPORTANT: To avoid paying for data that you don't need,
                // be sure to use Autocomplete.setFields() to specify only the place data that you will use.
                if (this.fields) {
                    this.$autocomplete.setFields(this.fields);
                }

                // Not using `bindEvents` because we also want
                // to return the result of `getPlace()`
                this.$autocomplete.addListener('place_changed', () => {
                    const placeDetail = this.$autocomplete.getPlace();
                    this.$emit('place-changed', placeDetail);
                    this.$emit("changeAddress", placeDetail.formatted_address);
                });
            });
        },
        computed: {
            google: gmapApi
        },
        methods: {
            changeValueAddress($evt) {
                console.log($evt.target.value);
                this.$emit("changeAddress", $evt.target.value);
            },
            onChangeInput($evt) {
                const str = $evt.target.value;
                const prefix = '';
                if(str.indexOf(prefix) === 0) {
                    console.log(str.indexOf(prefix));
                } else {
                    if (prefix.indexOf(str) >= 0) {
                        this.$refs.input.value = prefix;

                    } else {
                        this.$refs.input.value = prefix + $evt.target.value;
                    }
                }
            },
            simulateArrowDown(input) {
                // TODO: Analyze disabled eslint rules in the file
                const _addEventListener = input.addEventListener
                    ? input.addEventListener
                    : input.attachEvent;

                function addEventListenerWrapper(type, listener) {
                    // Simulate a 'down arrow' keypress on hitting 'return' when no pac suggestion is selected,
                    // and then trigger the original listener.
                    if (type === 'keydown') {
                        const origListener = listener;
                        listener = (event) => {
                            const suggestionSelected =
                                document.getElementsByClassName('pac-item-selected').length > 0;
                            if (event.which === 13 && !suggestionSelected) {
                                const simulatedEvent = document.createEvent('Event');
                                simulatedEvent.keyCode = 40;
                                simulatedEvent.which = 40;
                                origListener.apply(input, [simulatedEvent]);
                            }
                            origListener.apply(input, [event]);
                        };
                    }
                    _addEventListener.apply(input, [type, listener]);
                }

                input.addEventListener = addEventListenerWrapper;
                input.attachEvent = addEventListenerWrapper;
            },
            capitalizeFirstLetter(string) {
                return string.charAt(0).toUpperCase() + string.slice(1)
            },
            getPropsValues(vueInst, props) {
                return Object.keys(props).reduce((acc, prop) => {
                    if (vueInst[prop] !== undefined) {
                        acc[prop] = vueInst[prop];
                    }
                    return acc;
                }, {});
            },
            bindProps(vueInst, googleMapsInst, props) {
                Object.keys(props).forEach((attribute) => {
                    const { twoWay, type, trackProperties, noBind } = props[attribute];

                    if (!noBind) {
                        const setMethodName = `set${this.capitalizeFirstLetter(attribute)}`;
                        const getMethodName = `get${this.capitalizeFirstLetter(attribute)}`;
                        const eventName = `${attribute.toLowerCase()}_changed`;
                        const initialValue = vueInst[attribute];

                        if (typeof googleMapsInst[setMethodName] === 'undefined') {
                            throw new Error(
                                // TODO: Analyze all disabled rules in the file
                                `${setMethodName} is not a method of (the Maps object corresponding to) ${vueInst.$options._componentTag}`
                            );
                        }

                        // We need to avoid an endless
                        // propChanged -> event emitted -> propChanged -> event emitted loop
                        // although this may really be the user's responsibility
                        if (type !== Object || !trackProperties) {
                            // Track the object deeply
                            vueInst.$watch(
                                attribute,
                                () => {
                                    const attributeValue = vueInst[attribute];

                                    googleMapsInst[setMethodName](attributeValue);
                                },
                                {
                                    immediate: typeof initialValue !== 'undefined',
                                    deep: type === Object,
                                }
                            );
                        } else {
                            this.watchPrimitiveProperties(
                                vueInst,
                                trackProperties.map((prop) => `${attribute}.${prop}`),
                                () => {
                                    googleMapsInst[setMethodName](vueInst[attribute]);
                                },
                                vueInst[attribute] !== undefined
                            );
                        }

                        if (
                            twoWay &&
                            (vueInst.$gmapOptions.autobindAllEvents ||
                                vueInst.$listeners[eventName])
                        ) {
                            googleMapsInst.addListener(eventName, () => {
                                vueInst.$emit(eventName, googleMapsInst[getMethodName]());
                            });
                        }
                    }
                });
            },
            watchPrimitiveProperties(
                vueInst,
                propertiesToTrack,
                handler,
                immediate = false
            ) {
                let isHandled = false;

                function requestHandle() {
                    if (!isHandled) {
                        isHandled = true;
                        vueInst.$nextTick(() => {
                            isHandled = false;
                            handler();
                        });
                    }
                }

                propertiesToTrack.forEach((prop) => {
                    vueInst.$watch(prop, requestHandle, { immediate });
                });
            }
        }
    }
</script>

<style scoped>
    .inputAutocomplete {
        margin: 0;
        font-variant: tabular-nums;
        list-style: none;
        font-feature-settings: 'tnum';
        position: relative;
        display: inline-block;
        width: 100%;
        height: 32px;
        padding: 4px 11px;
        color: rgba(0, 0, 0, 0.65);
        font-size: 14px;
        line-height: 1.5;
        background-color: #fff;
        background-image: none;
        border: 1px solid #d9d9d9;
        border-radius: 4px;
        transition: all 0.3s;
    }
</style>
